import { useState } from 'react';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { ConfirmModalShell } from '../work-queue/tasks/approval-review/ConfirmModalShell';
import { ConfirmModalHeader } from '../work-queue/tasks/approval-review/ConfirmModalHeader';
import { ConfirmModalFooter } from '../work-queue/tasks/approval-review/ConfirmModalFooter';
import { ReasonField } from '../work-queue/tasks/approval-review/ReasonField';
import { MAX_CANCEL_REASON_LENGTH, validateCancelReason } from './virtualAppointmentCancelReason';

const MIN_CANCEL_REASON_LENGTH = 1;

interface VirtualAppointmentCancelModalProps {
    typeTitle: string;
    submitting: boolean;
    /** The API's refusal, shown above the buttons. */
    apiError: string | null;
    /** Receives the trimmed reason. */
    onSubmit: (reason: string) => void;
    onClose: () => void;
}

export function VirtualAppointmentCancelModal({
    typeTitle,
    submitting,
    apiError,
    onSubmit,
    onClose
}: VirtualAppointmentCancelModalProps) {
    const [reason, setReason] = useState('');
    const validation = validateCancelReason(reason);
    const lengthError = !validation.ok && reason.trim() !== '' ? validation.error : null;

    const handleConfirm = () => {
        if (validation.ok && !submitting) onSubmit(validation.reason);
    };

    return (
        <ConfirmModalShell widthClassName="w-[480px]" dismissible={!submitting} onDismiss={onClose}>
            <ConfirmModalHeader
                icon={XCircleIcon}
                iconClassName="w-5 h-5 text-red-500"
                title="Cancel virtual appointment"
                onClose={onClose}
                disabled={submitting}
            />
            <div className="px-5 py-4 flex flex-col gap-3">
                <p className="text-sm text-gray-800 font-semibold break-words">{typeTitle}</p>
                <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                    A canceled VA can&apos;t be reopened. Plan a new one if it&apos;s needed later.
                </p>
                <ReasonField
                    value={reason}
                    onChange={setReason}
                    disabled={submitting}
                    label="Why are you canceling?"
                    placeholder="e.g. Already covered in the last check-in."
                    minLength={MIN_CANCEL_REASON_LENGTH}
                    maxLength={MAX_CANCEL_REASON_LENGTH}
                />
                {lengthError && <p className="text-[11px] text-red-500">{lengthError}</p>}
                {apiError && (
                    <p
                        role="alert"
                        className="text-sm text-red-800 bg-red-50 border border-red-300 rounded-md px-3 py-2"
                    >
                        {apiError}
                    </p>
                )}
            </div>
            <ConfirmModalFooter
                onCancel={onClose}
                onConfirm={handleConfirm}
                cancelLabel="Keep VA"
                cancelDisabled={submitting}
                confirmDisabled={submitting || !validation.ok}
                confirmLabel={submitting ? 'Canceling…' : 'Cancel VA'}
                confirmColorClassName="bg-red-600 text-white hover:bg-red-700"
            />
        </ConfirmModalShell>
    );
}
